/**
 * Calendar arithmetic for the month grid. Pure, and it takes "today" as an argument.
 *
 * ── Why `today` is a parameter ──────────────────────────────────────────────
 *
 * The same reason `calc.ts` takes `currentSourceVersion` rather than importing it:
 * a function whose answer depends on a clock it reads for itself cannot be held
 * still by a test, and the interesting cases here are all edges — the last day of a
 * month, the first Monday, the day after today. `today` is a local date in the
 * **company's** timezone (`SessionUser.timezone`), resolved once by the screen with
 * `localDateIn`, because "today" is a different day in Hanoi and in Berlin and the
 * browser's zone is an accident of where somebody is sitting.
 *
 * ── Why days after today are their own kind ─────────────────────────────────
 *
 * `GET /attendance` will happily answer for a date in the future: `resolveDateRange`
 * only refuses `date_from > date_to` and a span over 366 days, and every
 * `(employee, date)` cell in the window comes back — a future one as
 * `data_incomplete` with `reason: 'no_session_row'`, because no session row exists
 * for a day that has not happened. Rendering that would put "Thiếu dữ liệu" on next
 * Tuesday.
 *
 * Task 42 met the same edge one unit down and its report states the rule: *"an hour
 * that has not happened and an hour that did not exist are not drawn at all"*. So
 * the month read is **clamped to today** and later days are `future` — no badge, no
 * request, and not a cell anybody can open.
 */

import { addDays } from '../i18n/datetime';

/** `YYYY-MM`. The unit the month picker moves in. */
export type Month = string;

const MONTH_PATTERN = /^(\d{4})-(0[1-9]|1[0-2])$/;
const DATE_PATTERN = /^(\d{4})-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/;

export function isMonth(value: string): boolean {
  return MONTH_PATTERN.test(value);
}

/** The `YYYY-MM` a `YYYY-MM-DD` belongs to. Throws rather than guessing. */
export function monthOf(date: string): Month {
  if (!DATE_PATTERN.test(date)) throw new Error(`not a local date: ${date}`);
  return date.slice(0, 7);
}

/** `YYYY-MM-01`. */
export function firstDayOf(month: Month): string {
  if (!isMonth(month)) throw new Error(`not a month: ${month}`);
  return `${month}-01`;
}

/**
 * The last day of the month, computed by stepping **back** one day from the first of
 * the next month rather than from a table of lengths — so February 2028 is the 29th
 * without this file knowing what a leap year is.
 */
export function lastDayOf(month: Month): string {
  return addDays(firstDayOf(shiftMonth(month, 1)), -1);
}

/** Move the month by whole months. Handles the December/January wrap. */
export function shiftMonth(month: Month, delta: number): Month {
  const parsed = MONTH_PATTERN.exec(month);
  if (parsed === null) throw new Error(`not a month: ${month}`);
  const year = Number(parsed[1]);
  const index = Number(parsed[2]) - 1 + delta;
  const nextYear = year + Math.floor(index / 12);
  const nextMonth = ((index % 12) + 12) % 12;
  return `${String(nextYear).padStart(4, '0')}-${String(nextMonth + 1).padStart(2, '0')}`;
}

/**
 * ISO weekday of a local date: Monday is 1, Sunday is 7.
 *
 * Through `Date.UTC` on the digits, never through the browser's zone: the input is a
 * calendar date with no time of day, so which weekday it is is arithmetic and not a
 * question about instants. Reading it in a zone drags the DST edge into a
 * subtraction that has no instants in it — the mistake `timezone.ts` avoids
 * server-side for the same reason.
 */
export function isoWeekdayOf(date: string): number {
  const parsed = DATE_PATTERN.exec(date);
  if (parsed === null) throw new Error(`not a local date: ${date}`);
  const utc = Date.UTC(Number(parsed[1]), Number(parsed[2]) - 1, Number(parsed[3]));
  const sundayZero = new Date(utc).getUTCDay();
  return sundayZero === 0 ? 7 : sundayZero;
}

/**
 * What one square of the grid is.
 *
 * `pad` exists so the seven columns line up; it is **not** a day of any month and it
 * carries no date. `future` is a real date of this month that has not happened yet.
 * `day` is a date the server was asked about.
 */
export type CellKind = 'pad' | 'day' | 'future';

export interface MonthCell {
  readonly kind: CellKind;
  /** `null` only for `pad`. */
  readonly date: string | null;
  /** Day of the month, for the label. `null` only for `pad`. */
  readonly dayOfMonth: number | null;
}

/**
 * The month as weeks of seven, Monday first.
 *
 * Monday first because the audience is Vietnamese and the week is written T2…CN;
 * the header labels come from the string catalogue, and the ordering is here so that
 * the two cannot disagree.
 *
 * Leading and trailing squares are `pad` rather than the neighbouring months' days.
 * Showing 30 September in an August grid invites a click that reads a day the
 * request never covered, and a cell that answers for a window it is not in is worse
 * than a blank.
 */
export function monthGrid(month: Month, today: string): readonly (readonly MonthCell[])[] {
  const first = firstDayOf(month);
  const last = lastDayOf(month);
  const lead = isoWeekdayOf(first) - 1;
  const cells: MonthCell[] = [];
  for (let index = 0; index < lead; index += 1) {
    cells.push({ kind: 'pad', date: null, dayOfMonth: null });
  }
  for (let date = first; date <= last; date = addDays(date, 1)) {
    cells.push({
      kind: date > today ? 'future' : 'day',
      date,
      dayOfMonth: Number(date.slice(8, 10)),
    });
  }
  while (cells.length % 7 !== 0) cells.push({ kind: 'pad', date: null, dayOfMonth: null });

  const weeks: MonthCell[][] = [];
  for (let index = 0; index < cells.length; index += 7) weeks.push(cells.slice(index, index + 7));
  return weeks;
}

/**
 * The window to ask the server for, or `null` when the whole month is in the future.
 *
 * `to` is `min(last day of month, today)`. `null` rather than an empty range because
 * "there is nothing to ask yet" is not an empty result — the same distinction
 * `useApiQuery`'s `path === null` makes, and for the same reason.
 */
export function monthRange(month: Month, today: string): { from: string; to: string } | null {
  const from = firstDayOf(month);
  if (from > today) return null;
  const last = lastDayOf(month);
  return { from, to: last < today ? last : today };
}

/**
 * How many days that window covers — the `limit` the month read needs.
 *
 * The grid is paged **employee-major** (`offset / range.days` in
 * `attendance-service.ts`), so one employee's month is exactly this many rows and a
 * limit below it would silently truncate the end of the month. Bounded by the
 * endpoint's own maximum of 200, which 31 never approaches; the clamp is there so
 * that a caller passing a wider range gets a refusal from the server rather than a
 * page that quietly stops.
 */
export function dayCount(range: { readonly from: string; readonly to: string }): number {
  let count = 0;
  for (let date = range.from; date <= range.to; date = addDays(date, 1)) count += 1;
  return count;
}
